import type { ConnectionPoolConfig } from "./constants";
import {
  CONNECTION_POOL_CONFIG,
  LOCAL_CONNECTION_POOL_CONFIG,
  READ_ONLY_CONNECTION_POOL_CONFIG,
} from "./constants";
import { getSecret } from "./secrets";

// Mirrors apps/web/src/config.ts's IS_LOCAL; duplicated here (rather than
// imported) since @marble/db must stay usable from every runtime, including
// ones that never depend on apps/web.
const IS_LOCAL =
  process.env.NODE_ENV === "development" || process.env.IS_LOCAL === "true";

export const getDatabaseUrl = async (): Promise<string> => {
  let baseUrl: string;

  if (process.env.POSTGRES_URL) {
    baseUrl = process.env.POSTGRES_URL;
  } else {
    if (!process.env.DB_SECRET_ARN) {
      throw new Error("Missing DB_SECRET_ARN in env");
    }

    baseUrl = await _buildUrlFromSecret(process.env.DB_SECRET_ARN);
  }

  return _withConnectionParams(
    baseUrl,
    IS_LOCAL ? LOCAL_CONNECTION_POOL_CONFIG : CONNECTION_POOL_CONFIG,
  );
};

/**
 * Resolves the URL of the read-only database, or `null` when none is
 * configured -- local dev, preview, and any stage without one -- so callers
 * can fall back to the primary client.
 *
 * `READ_ONLY_DB_HOST` is injected by the infrastructure (see
 * `stacks/database.ts`), so no stage has to set it by hand. The other two
 * are escape hatches, in precedence order:
 *
 *   - `READ_ONLY_POSTGRES_URL`  -- full connection string, for local testing
 *   - `READ_ONLY_DB_SECRET_ARN` -- Secrets Manager secret, same shape as
 *     `DB_SECRET_ARN`, for when the read-only database has its own user
 *   - `READ_ONLY_DB_HOST`       -- host override reusing the primary
 *     credentials
 *
 * `READ_ONLY_DB_STATEMENT_TIMEOUT_MS` overrides the per-connection statement
 * timeout; "0" disables it.
 */
export const getReadOnlyDatabaseUrl = async (): Promise<string | null> => {
  let baseUrl: string;

  if (process.env.READ_ONLY_POSTGRES_URL) {
    baseUrl = process.env.READ_ONLY_POSTGRES_URL;
  } else if (process.env.READ_ONLY_DB_SECRET_ARN) {
    baseUrl = await _buildUrlFromSecret(process.env.READ_ONLY_DB_SECRET_ARN);
  } else if (process.env.READ_ONLY_DB_HOST) {
    const primaryUrl = new URL(await getDatabaseUrl());
    primaryUrl.hostname = process.env.READ_ONLY_DB_HOST;
    baseUrl = primaryUrl.toString();
  } else {
    return null;
  }

  return _withConnectionParams(baseUrl, {
    ...READ_ONLY_CONNECTION_POOL_CONFIG,
    statement_timeout_ms:
      process.env.READ_ONLY_DB_STATEMENT_TIMEOUT_MS ??
      READ_ONLY_CONNECTION_POOL_CONFIG.statement_timeout_ms,
  });
};

const _buildUrlFromSecret = async (secretArn: string): Promise<string> => {
  const secretString = await getSecret(secretArn);

  const secrets = JSON.parse(secretString) as any;
  const { host, username, password, port, dbname } = secrets;
  if (!host) {
    throw new Error("Missing host in secrets");
  }

  return `postgresql://${username}:${password}@${host}:${port}/${dbname}`;
};

const _withConnectionParams = (
  baseUrl: string,
  config: ConnectionPoolConfig,
): string => {
  const url = new URL(baseUrl);
  url.searchParams.set("connection_limit", config.connection_limit);
  url.searchParams.set("pool_timeout", config.pool_timeout);
  url.searchParams.set("connect_timeout", config.connect_timeout);

  if (config.statement_timeout_ms && config.statement_timeout_ms !== "0") {
    url.searchParams.set(
      "options",
      `-c statement_timeout=${config.statement_timeout_ms}`,
    );
  }
  return url.toString();
};
